/**
 * 处理全部导入的路由模块
 * @param modules - 路由模块
 */
export function handleImportRoutes(modules) {
  return Object.keys(modules).map(key => {
    return modules[key].default;
  });
}

/**
 * 将层级结构的路由配置拍平，用于渲染
 * @param routes - 路由
 */
export function flattenRoutes(routes) {
  if (!routes || routes.length == 0) {
    return [];
  }
  let flatList = [];
  function flatten(route, parentPath) {
    const { path, i18nTitle, authName, meta, component, children } = route;
    const fullPath = !path.startsWith('/') && parentPath ? `${parentPath}/${path}` : path;
    flatList.push({
      fullPath,
      i18nTitle,
      authName,
      component,
      children,
      meta,
    });
    if (children?.length > 0) {
      children.forEach(childRoute => flatten(childRoute, fullPath));
    }
  }
  routes.forEach(route => flatten(route));
  return flatList;
}

/**
 * 将层级结构的路由配置拍平，拼接各层级路由的菜单
 * @param routes - 路由
 * todo 支处理1-3级页面路由
 */
export function flattenRoutesTitle(routes) {
  if (!routes || routes.length == 0) {
    return [];
  }
  let flatList = [];
  function flatten(route, parentPath = '', prefixTitleArr = []) {
    if (route.meta?.hideInMenu) return [];
    const { path, i18nTitle, children } = route;
    const tempItem = {
      value: !path.startsWith('/') && parentPath ? `${parentPath}/${path}` : path,
      titleList: [...prefixTitleArr, i18nTitle],
      label: [...prefixTitleArr, i18nTitle].join(' > '),
    };
    flatList.push(tempItem);
    if (tempItem.titleList.length < 3 && children?.length > 0) {
      children.forEach(childRoute => flatten(childRoute, tempItem.value, tempItem.titleList));
    }
  }
  routes.forEach(route => flatten(route));
  return flatList;
}

/**
 * 获取路由层级
 */
export function getRouteLevel(path = '') {
  return path.split('/').length - 1;
}

/**
 * 将路由配置+结合权限信息 转换成菜单
 * (递归处理所有层级的数据，但菜单组件仅渲染到3级菜单)
 * @param routes - 路由
 */
export function transformRoutesToMenu(routes, flatAuth, parentPath) {
  if (!routes || routes.length === 0 || !flatAuth) {
    return [];
  }
  const resultMenu = [];
  routes.forEach(route => {
    const { i18nTitle, authName, path, component, meta, children } = route;
    const fullPath = !path.startsWith('/') && parentPath ? `${parentPath}/${path}` : path;
    const hasComponent =
      getRouteLevel(fullPath) < 3 ? Boolean(component) : Boolean(component || children?.length);

    const menuItem = {
      i18nTitle,
      fullPath: hasComponent ? fullPath : '#', // 没有配置component的 不需要跳转
      meta,
      children: transformRoutesToMenu(route.children, flatAuth, fullPath),
    };
    if (meta?.hideInMenu) return; // 无需展示在菜单
    if (Boolean(authName) && !flatAuth[authName]) return; // 无权限, 无需push进菜单
    resultMenu.push(menuItem);
  });
  return resultMenu;
}

/**
 * 查找菜单路由数组中，第一个不为#的fullpath
 * 动态查找用户的首页
 */
export const findFirstPath = array => {
  for (const item of array) {
    // 检查当前对象的 fullPath
    if (item.fullPath !== '#') {
      return item; // 如果找到，返回这个对象
    }

    // 如果有子元素，递归查找
    if (item.children && item.children.length) {
      const found = findFirstPath(item.children);
      if (found) {
        return found; // 返回找到的对象
      }
    }
  }
  return null; // 如果没有找到，返回 null
};

/**
 * 根据路由信息，生成(4/5级)菜单数据
 */
export const transformRoutesToTabMenu = params => {
  const { menuLevel, handleFullpathFn, pathname, pathRouteMap, flatRoutes } = params;
  const res = getRoutesByPath(pathname, pathRouteMap, flatRoutes);
  const curLevelRoute = res[menuLevel - 2];
  const tempList = [];
  // n级菜单的children就是n+1级菜单数据
  curLevelRoute?.children?.forEach(item => {
    if (!item.meta?.hideInMenu) {
      tempList.push({
        ...item,
        fullPath: handleFullpathFn
          ? handleFullpathFn(curLevelRoute.fullPath, item.path)
          : `${curLevelRoute.fullPath}/${item.path}`,
      });
    }
  });
  return tempList;
};

/**
 * 权限路由排序
 * @param routes - 权限路由数组
 */
export function sortRoutes(routes) {
  const res = routes.sort(
    (next, pre) => Number(next.meta?.order || 9999) - Number(pre.meta?.order || 9999)
  );
  return res;
}

/**
 * 将拍平的路由数组，转化为pathMap对象
 * 在某些场景下，支持快速索引
 */
export function transformFlatRoutesToPathMap(routes) {
  const res = {};
  for (let route of routes) {
    if (route.meta?.hideInMenu) continue;
    res[route.fullPath] = route;
  }
  return res;
}

/**
 * 判断实际路由和定义路由是否相等
 * 支持动态路由语法
 */
export function isMatchRoutePattern(routePattern, path) {
  if (!routePattern || !path) return false;
  const regex = routePattern.replace(/:[^\s/]+/g, '([^/]+)').replace(/\//g, '\\/'); // 将":id"等形式替换为正则捕获组、转义斜杠
  const routeRegex = new RegExp(`^${regex}$`);
  return routeRegex.test(path);
}

/**
 * 根据当前path, 获取每一层的路由信息
 * @param {*} path 当前路由
 * @param {*} pathRouteMap route stores里的map, 方便快速查找
 * @param {*} flatRoutes 数组routes，兜底在pathRouteMap查找不到时，用正则进行遍历匹配
 * @returns
 */
export function getRoutesByPath(path, pathRouteMap, flatRoutes) {
  const pathList = path.split('/');
  if (pathList.length < 2) return [];
  pathList.shift(); // 移除第一个 '/'

  let prefix = '';
  const res = [];
  for (let path of pathList) {
    path = path.replace(/:[^/]*?(?=\/|$)/g, ''); // 移除动态id
    if (!path) continue;
    const curPath = prefix + '/' + path;

    // 索引普通路由
    if (pathRouteMap[curPath]) {
      res.push(pathRouteMap[curPath]);
      prefix = curPath;
      continue;
    }

    // 索引动态参数路由
    const matchRes = flatRoutes.find(routeItem => {
      return isMatchRoutePattern(routeItem.fullPath, curPath);
    });
    matchRes && res.push(matchRes);
    prefix = curPath;
  }
  return res;
}

/**
 * 重新向到登录页
 */
export function toLoginPage(needRedirect) {
  const currentPath = window.location.pathname + window.location.search;

  if (!/\/login/.test(window.location.pathname)) {
    window.location.href = needRedirect
      ? `/login?redirect=${encodeURIComponent(currentPath)}`
      : `/login`;
  }
}
